import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuthStore, useCartStore } from '../lib/store';
import { formatPrice } from '../lib/utils';
import { ShoppingBag, Check } from 'lucide-react';
import { toast } from 'sonner';

export const ProductCard = ({ product, onAuthRequired }) => {
  const { isAuthenticated } = useAuthStore();
  const { addToCart } = useCartStore();
  const [isAdding, setIsAdding] = React.useState(false);
  const [added, setAdded] = React.useState(false);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      onAuthRequired?.();
      return;
    }

    setIsAdding(true);
    const success = await addToCart(product.id);
    setIsAdding(false);
    
    if (success) {
      setAdded(true);
      toast.success(`${product.name} added to cart`);
      setTimeout(() => setAdded(false), 2000);
    } else {
      toast.error('Failed to add to cart');
    }
  };

  return (
    <Link to={`/products/${product.id}`} className="group" data-testid={`product-card-${product.id}`}>
      <div className="bg-white border border-stone-100 rounded-lg overflow-hidden transition-all duration-300 hover:shadow-warm-lg hover:-translate-y-1">
        <div className="aspect-square overflow-hidden bg-surface">
          <img 
            src={product.image_url} 
            alt={product.name}
            className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="p-5">
          <span className="font-accent text-primary text-lg">{product.category}</span>
          <h3 className="font-heading text-xl font-semibold text-secondary mt-1 group-hover:text-primary transition-colors">
            {product.name}
          </h3>
          <p className="text-muted-foreground text-sm mt-2 line-clamp-2">
            {product.description}
          </p>
          <div className="flex items-center justify-between mt-4">
            <span className="text-xl font-bold text-primary">{formatPrice(product.price)}</span>
            <Button 
              size="sm" 
              onClick={handleAddToCart}
              disabled={isAdding || !product.in_stock}
              className="transition-all duration-200"
              data-testid={`add-to-cart-${product.id}`}
            >
              {added ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Added
                </>
              ) : (
                <>
                  <ShoppingBag className="w-4 h-4 mr-1" />
                  {product.in_stock ? 'Add' : 'Out of Stock'}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Link>
  );
};
